import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { fetchCraftRoomPage } from '../api/fetchers';
import type { CraftRoomResponse, MaterialResponse } from '@mrb91002/shared-types';

interface CraftRoomContextType {
  craftRoom: CraftRoomResponse | null;
  materials: MaterialResponse[];
  projects: any[];
  loading: boolean;
  error: string | null;
  refetchCraftRoom: () => Promise<void>;
  hasMaterial: (materialId: string) => boolean;
  updateMaterials: (updater: (materials: MaterialResponse[]) => MaterialResponse[]) => void;
}

const CraftRoomContext = createContext<CraftRoomContextType | undefined>(undefined);

export const CraftRoomProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, screen_name } = useAuth();
  const [craftRoom, setCraftRoom] = useState<CraftRoomResponse | null>(null);
  const [materials, setMaterials] = useState<MaterialResponse[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCraftRoom = async () => {
    if (!user || !screen_name) return;
    
    try {
      setLoading(true);
      setError(null);
      const pageData = await fetchCraftRoomPage(screen_name);
      setCraftRoom(pageData.craftRoom);
      setMaterials(pageData.craftRoom.materials || []);
      setProjects(pageData.projects || []);
    } catch (err) {
      console.error('Failed to load craft room:', err);
      setError(err instanceof Error ? err.message : 'Failed to load craft room');
    } finally {
      setLoading(false);
    }
  };

  const refetchCraftRoom = async () => {
    await fetchCraftRoom();
  };

  const hasMaterial = (materialId: string): boolean => {
    return materials.some(m => 
      m.material_id === materialId || 
      m.public_material_ref === materialId
    );
  };

  const updateMaterials = (updater: (materials: MaterialResponse[]) => MaterialResponse[]) => {
    setMaterials(updater);
  };

  // Auto-fetch when user logs in
  useEffect(() => {
    if (user && screen_name && !craftRoom) {
      fetchCraftRoom();
    }
  }, [user, screen_name]);

  // Clear data when user logs out
  useEffect(() => {
    if (!user) {
      setCraftRoom(null);
      setMaterials([]);
      setProjects([]);
      setError(null);
    }
  }, [user]);

  return (
    <CraftRoomContext.Provider value={{
      craftRoom,
      materials,
      projects,
      loading,
      error,
      refetchCraftRoom,
      hasMaterial,
      updateMaterials
    }}>
      {children}
    </CraftRoomContext.Provider>
  );
};

export const useCraftRoom = () => {
  const context = useContext(CraftRoomContext);
  if (context === undefined) {
    throw new Error('useCraftRoom must be used within a CraftRoomProvider');
  }
  return context;
};